import clsx from 'clsx';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { Spin } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import packageJson from '../../package.json';
import IndeterminateProgress from '../components/Common/IndeterminateProgress';
import TagsList from '../components/Common/TagsList';
import TransferProgressDrawers from '../components/Common/TransferProgressDrawers';
import ErrorBoundary from '../components/Core/ErrorBoundary';
import Header from '../components/Core/Header';
import { Icon } from '../components/Core/Icon';
import MainButtonMenu from '../components/Core/MainButtonMenu';
import RightPanel from '../components/Core/RightPanel';
import Sidebar from '../components/Core/Sidebar';
import { EventBusEventEnum } from '../enums/EventBusEventEnum';
import { useAuth } from '../hooks/useAuth';
import { useRightPanel } from '../hooks/useRightPanel';
import { usePermissions } from '../hooks/usePermissions';
import EventBus from '../services/EventBus';
import { filteredRouterPages } from '../utils/routerPages';

function MainLayout() {
  const location = useLocation();
  const { receivedFilesCount } = usePermissions();
  const { isOnBehalfOfUser, authenticatedUser, isAdminOrContributor } =
    useAuth();
  const {
    rightPanelContent,
    isRightPanelOpen,
    closeRightPanel,
    hasRightPanelCloseButton,
    isRightPanelCheckDirtyOnClose,
  } = useRightPanel();
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  const [isGlobalLoaderVisible, setIsGlobalLoaderVisible] = useState(false);

  const routerPageContext = useMemo(
    () => ({
      authenticatedUser,
      isOnBehalfOfUser,
    }),
    [authenticatedUser, isOnBehalfOfUser]
  );

  /**
   * Memoized array of updated router pages after applying filters and transformations.
   *
   * The `updatedRouterPages` variable is derived from the `routerPages` array by performing the following operations:
   * - Filters out pages where the `isHidden` property is set to `true`.
   * - Adds or modifies specific properties for certain pages based on their `key` value:
   *   - For pages with the `key` of 'shared-with-me', it adds `notificationsCount` using `receivedFilesCount`
   *     and sets `isDisabled` based on `isOnBehalfOfUser`.
   *   - For pages with the `key` of 'settings', it only sets `isDisabled` based on `isOnBehalfOfUser`.
   *
   * Dependencies for memoization include:
   * - `receivedFilesCount`
   */
  const updatedRouterPages = useMemo(() => {
    return filteredRouterPages(routerPageContext)
      .filter((page) => !page.isHidden)
      .map((page) => {
        if (page.key === 'received') {
          return {
            ...page,
            notificationsCount: receivedFilesCount,
            isDisabled: isOnBehalfOfUser,
          };
        }

        if (page.key === 'settings') {
          return {
            ...page,
            isDisabled: isOnBehalfOfUser,
          };
        }
        return page;
      });
  }, [receivedFilesCount]);

  /**
   * @description Close the right panel on location change.
   */
  useEffect(() => {
    if (location.pathname !== prevPathname && isRightPanelOpen) {
      closeRightPanel();
    }

    setPrevPathname(location.pathname);
  }, [location.pathname, isRightPanelOpen, closeRightPanel, prevPathname]);

  useEffect(() => {
    const handler = (data: any) => {
      setIsGlobalLoaderVisible(data.isVisible);
    };
    EventBus.on(EventBusEventEnum.IsGlobalLoaderVisible, handler);
    return () => {
      EventBus.off(EventBusEventEnum.IsGlobalLoaderVisible, handler);
    };
  }, []);

  return (
    <>
      <div className="flex-grow-1 flex min-h-full w-full overflow-hidden bg-background">
        <Header />

        {isGlobalLoaderVisible && (
          <IndeterminateProgress className="absolute left-0 top-14 z-[9999] w-full p-0" />
        )}
        <Sidebar>
          <MainButtonMenu context={routerPageContext} />
          <div className="flex flex-1 flex-col gap-6 overflow-x-hidden">
            <ul role="list" className="flex flex-col">
              {updatedRouterPages.map((link) => (
                <li
                  key={link.key}
                  aria-disabled={link.isDisabled}
                  className={clsx(
                    'group pb-3',
                    link.isDisabled &&
                      'pointer-events-none text-base opacity-50'
                  )}
                >
                  <Link to={link.href}>
                    <div
                      className={clsx(
                        'duration-400 relative flex flex-row items-center px-1 pt-1 text-blue-dark/70 transition-all lg:gap-2',
                        link.hasDivider && 'border-b border-gray-medium pb-3'
                      )}
                    >
                      {link.notificationsCount > 0 && (
                        <div className="absolute left-10 top-1 flex h-3 w-3 items-center justify-center rounded-full bg-red text-center">
                          <span className="text-[0.5rem] leading-[1] text-white">
                            {link.notificationsCount}
                          </span>
                        </div>
                      )}
                      <div
                        className={clsx(
                          'h-[20px] w-1 rounded-full transition-all duration-200 ease-in-out lg:mr-2',
                          location.pathname.startsWith(link.href)
                            ? 'bg-blue-dark'
                            : 'group-hover:bg-gray-medium'
                        )}
                      />
                      <Icon name={link.icon} className="m-auto w-10" />
                      <span
                        className={clsx(
                          'my-auto hidden w-full truncate pl-1.5 text-lg lg:block',
                          location.pathname.startsWith(link.href) &&
                            'text-azure'
                        )}
                      >
                        {link.label}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <TagsList />
            <div className="flex flex-1 items-end justify-center">
              <span className="text-center text-xs text-gray-dark">
                v{packageJson.version}
              </span>
            </div>
          </div>
        </Sidebar>
        <main
          className={clsx(
            'relative ml-[60px] mr-4 mt-14 w-[100vw] flex-1 flex-shrink overflow-x-hidden py-4 transition-all duration-300 lg:ml-[205px]',
            isRightPanelOpen ? 'md:mr-[355px]' : 'mr-0'
          )}
        >
          <div className="h-full flex-1">
            <ErrorBoundary>
              <OverlayScrollbarsComponent
                options={{
                  scrollbars: {
                    autoHide: 'leave',
                  },
                }}
                defer
                style={{ height: '100%', overflow: 'auto' }}
              >
                <Suspense
                  fallback={
                    <div
                      className={clsx(
                        'transition-property relative mt-16 h-full flex-1 overflow-hidden rounded-md border bg-white'
                      )}
                    >
                      <div className="flex h-full flex-col items-center justify-center">
                        <Spin />
                      </div>
                    </div>
                  }
                >
                  <Outlet />
                </Suspense>
              </OverlayScrollbarsComponent>
            </ErrorBoundary>
          </div>
        </main>
        <RightPanel
          isOpen={isRightPanelOpen}
          onClose={closeRightPanel}
          hasCloseButton={hasRightPanelCloseButton}
          checkDirtyOnClose={isRightPanelCheckDirtyOnClose}
        >
          {rightPanelContent}
        </RightPanel>
        <TransferProgressDrawers />
      </div>
    </>
  );
}

export default MainLayout;
